import type { SlotGameStore, BaseComponentConfig } from '@gamzix/slot_core';
import { CONST } from '../../../src/const';
import { transitionFSPrefab, secondTransitionFSPrefab } from '../prefabs/transition-fs.prefab';

class FreeSpinsGameComponent extends SlotGame.src.component.FreeSpinsGameComponent {
    static events = {
        START_INTRO_TRANSITION: 'FreeSpinsGameComponent.START_INTRO_TRANSITION',
        START_HIDE_INTRO_POPUP: 'FreeSpinsGameComponent.START_HIDE_INTRO_POPUP',
        END_INTRO_TRANSITION: 'FreeSpinsGameComponent.END_INTRO_TRANSITION',

        START_OUTRO_TRANSITION: 'FreeSpinsGameComponent.START_OUTRO_TRANSITION',
        START_HIDE_OUTRO_POPUP: 'FreeSpinsGameComponent.START_HIDE_OUTRO_POPUP',
        END_OUTRO_TRANSITION: 'FreeSpinsGameComponent.END_OUTRO_TRANSITION',

        START_ADDITIONAL_POPUP: 'FreeSpinsGameComponent.START_ADDITIONAL_POPUP',

        OUTRO_POPUP_START_COUNT: 'FreeSpinsGameComponent.OUTRO_POPUP_START_COUNT',
        OUTRO_POPUP_END_COUNT: 'FreeSpinsGameComponent.OUTRO_POPUP_END_COUNT',
    };

    protected tsAnim = null;
    protected introPopupAnim = null;
    protected outroPopupAnim = null;
    protected outroZeroPopupAnim = null;
    protected outroPopupContainer = null;
    protected tsPopupContainer = null;
    protected textContainer = null;

    override onGameReady() {
        super.onGameReady();
        this.tsPopupContainer = game.tree.findByName('freeSpinsGame.intro_popup_container');
        this.textContainer = game.tree.findByName('freeSpinsGame.intro_popup_container.wrapper_text_container');
        this.outroPopupAnim = game.tree.findByName('freeSpinsGame.outro_popup_spine');
        this.outroZeroPopupAnim = game.tree.findByName('freeSpinsGame.outro_popup_zero_spine');
        this.outroPopupContainer = game.tree.findByName('freeSpinsGame.outro_popup_container');
    }

    override isStarted() {
        return this.store.gameData.freeSpinsGame.isStarted() || this.isAddedFreeSpins;
    }

    constructor(config: BaseComponentConfig) {
        super(config);

        this.status_checkers = {
            ...super.getStatusCheckers(),
            isEnglishLang: () => (<SlotGameStore>game.store).language === 'en',
        };

        this.text_checkers = {
            ...super.getTextCheckers(),

            GET_TOTAL_WIN_NUMBER: () => this.store.toFormatMoney(this.store.gameData.winAmount),
            FREE_SPINS_AMOUNT: () => {
                return this.isAddedFreeSpins ? this.store.gameData.freeSpinsGame.added : 10;
            },
            GET_MULTIPLIER: () => `${this.store.gameData.freeSpinsGame.extra.wildMultiplier}`,
            GET_EXTRA_FREESPINS: () => `${this.store.gameData.freeSpinsGame.added}`,
        };
    }
    async showAdditionalPopup() {
        const container = game.tree.findByName('freeSpinsGame.add_popup_container');
        const text = game.tree.findByName('freeSpinsGame.add_popup_container.extra_freeSpins');
        text.forceViewUpdate();

        game.emit(FreeSpinsGameComponent.events.START_ADDITIONAL_POPUP);

        await game.utils.GameUtils.playAlphaAnimation(container, { from: 0, to: 1, time: 500, timeWait: 0 });

        game.emit('UIComponent.SET_PARENT_GROUP_FOR_SKIPPING', { parentGroupName: 'TOP' });
        await game.utils.cancelableDelay(2000);
        game.emit('UIComponent.SET_PARENT_GROUP_FOR_SKIPPING', { parentGroupName: 'OVERLAY' });

        await game.utils.GameUtils.playAlphaAnimation(container, { from: 1, to: 0, time: 500, timeWait: 0 });
    }

    setElementVisibility(element, isVisible) {
        if (!element) return;
        if (element.options.visible !== isVisible) {
            element.options.visible = isVisible;
            element.updateViewOptions();
        }
    }

    async showStartPopup() {
        if (this.isAddedFreeSpins) {
            await this.showAdditionalPopup();
            return;
        }
        game.emit(FreeSpinsGameComponent.events.START_INTRO_TRANSITION);

        if (!this.tsAnim || !this.introPopupAnim) {
            const isTsanimLoaded = game.builder.isResourcesLoaded({ type: 'SpineByName', name: 'transition_fs' });
            const isSecondTransitionisLoading = game.builder.isResourcesLoaded({
                type: 'SpineByName',
                name: 'FS_transition',
            });

            if (isTsanimLoaded && isSecondTransitionisLoading) {
                const transitionContainer = game.tree.findByName('freeSpinsGame.wrapper_transition_container');

                this.tsAnim = transitionFSPrefab(game);
                this.introPopupAnim = secondTransitionFSPrefab(game);

                transitionContainer.addChild(this.introPopupAnim);
                transitionContainer.addChild(this.tsAnim);

                await this.runIntoTransition();
            } else {
                await this.runIntroTransitionFade();
            }
        } else {
            await this.runIntoTransition();
        }

        game.emit(FreeSpinsGameComponent.events.END_INTRO_TRANSITION);
    }

    async runIntoTransition(): Promise<void> {
        this.setElementVisibility(this.tsAnim, true);
        const animName = this.status_checkers.isPortrait() ? 'transition_desktop' : 'transition_portrait';
        this.tsAnim.stop();
        this.tsAnim.playAnimation(animName, false);

        const textWild = game.tree.findByName('freeSpinsGame.intro_popup_container.wild_multipliers');
        const textScatter = game.tree.findByName('freeSpinsGame.intro_popup_container.freeSpins_additional');
        textWild.forceViewUpdate();
        // for update FREE_SPINS_AMOUNT
        this.onChangeOrientation();

        await game.utils.delay(1160);
        game.utils.GameUtils.playAlphaAnimation(this.tsPopupContainer, { from: 0, to: 1, time: 300, timeWait: 0 }); // TTTTTTTT
        game.utils.GameUtils.playAlphaAnimation(this.textContainer, { from: 0, to: 1, time: 300, timeWait: 0 });

        this.setElementVisibility(this.introPopupAnim, true);
        const skinAnimName = `x${this.store.gameData.freeSpinsGame.extra.wildMultiplier}`;
        this.introPopupAnim.hasSkin(skinAnimName) && this.introPopupAnim.setSkin(skinAnimName);
        this.introPopupAnim.stop();
        this.introPopupAnim.playAnimation('appearance', false);

        this.addChildSpineSlot(this.introPopupAnim, '3-10EXTRAFREESPINS', textScatter);
        this.addChildSpineSlot(this.introPopupAnim, 'x2/x3/x5WILD', textWild);

        await game.utils.delay(600); // TTTTTTTT

        this.introPopupAnim.playAnimation('idle', true);

        game.emit('UIComponent.SET_PARENT_GROUP_FOR_SKIPPING', { parentGroupName: 'TOP' });
        await game.utils.cancelableDelay(10000);
        game.emit('UIComponent.SET_PARENT_GROUP_FOR_SKIPPING', { parentGroupName: 'OVERLAY' });

        this.introPopupAnim.playAnimation('dissappearance', false);
        game.emit(FreeSpinsGameComponent.events.START_HIDE_INTRO_POPUP); // TTTTTTTT
        game.utils.GameUtils.playAlphaAnimation(this.tsPopupContainer, { from: 1, to: 0, time: 300, timeWait: 0 }); // TTTTTTTT
        game.utils.GameUtils.playAlphaAnimation(this.textContainer, { from: 1, to: 0, time: 300, timeWait: 0 });
        await game.utils.delay(600); // TTTTTTTT
        this.setElementVisibility(this.introPopupAnim, false);
    }

    addChildSpineSlot(elementSpine, slotName, child) {
        const slotIndex = elementSpine.spine.skeleton.findSlotIndex(slotName);
        const textContainer = game.builder.create(
            {
                type: "Container", 
                name: "pixi_text_container"
            }
        );

        //@ts-ignore
        textContainer.addChild(child)
        elementSpine.spine.slotContainers[slotIndex].children[0].addChild(textContainer)
    }

    async runIntroTransitionFade(): Promise<void> {
        const fader = game.tree.findByName('freeSpinsGame.intro_popup_container.bg_fade_graphics');
        fader.options.visible = true;
        game.utils.GameUtils.playAlphaAnimation(this.tsPopupContainer, { from: 0, to: 1, time: 300, timeWait: 0 });
        await game.utils.delay(2000);
        game.emit(FreeSpinsGameComponent.events.START_HIDE_INTRO_POPUP);
        game.utils.GameUtils.playAlphaAnimation(this.tsPopupContainer, { from: 1, to: 0, time: 300, timeWait: 0 });
        fader.options.visible = false;
    }

    async showFinishPopup() {
        if (this.store.gameData.winAmount === this.store.gameData.bet * CONST.MAX_WIN_CAP) {
            return;
        }
        game.emit(FreeSpinsGameComponent.events.START_OUTRO_TRANSITION);

        await this.showOutroPopup();

        game.emit(FreeSpinsGameComponent.events.END_OUTRO_TRANSITION);
    }
    async showOutroZeroWinPopup() {
        const container = game.tree.findByName('freeSpinsGame.outro_popup_container_zero_win');
        container.options.visible = true;
        this.setElementVisibility(this.outroZeroPopupAnim, true);
        this.outroZeroPopupAnim.stop();
        this.outroZeroPopupAnim.playAnimation('FS_appearance', false);

        await game.utils.GameUtils.playAlphaAnimation(container, { from: 0, to: 1, time: 300, timeWait: 0 });

        this.outroZeroPopupAnim.playAnimation('FS_idle', true);
        game.emit('UIComponent.SET_PARENT_GROUP_FOR_SKIPPING', { parentGroupName: 'TOP' });
        await game.utils.cancelableDelay(3000);
        game.emit('UIComponent.SET_PARENT_GROUP_FOR_SKIPPING', { parentGroupName: 'OVERLAY' });

        await game.utils.delay(500);
        game.emit(FreeSpinsGameComponent.events.START_HIDE_OUTRO_POPUP);
        this.outroZeroPopupAnim.playAnimation('FS_dissappearance', false);
        this.setElementVisibility(this.outroZeroPopupAnim, false);
        await game.utils.GameUtils.playAlphaAnimation(container, { from: 1, to: 0, time: 300, timeWait: 0 });
    }

    async showOutroPopup() {
        if (this.store.gameData.winAmount === 0) {
            await this.showOutroZeroWinPopup();
            return;
        }
        const winText = game.tree.findByName('freeSpinsGame.outro_popup_container.free_spin_win_text');
        const freeSpinsText = game.tree.findByName('freeSpinsGame.outro_popup_container.free_spin_outro');
        const totalWin = game.tree.findByName('freeSpinsGame.outro_popup_container.total_win_value');

        totalWin.options.text = this.store.toFormatMoney(1);
        totalWin.updateViewOptions();

        this.outroPopupContainer.visible = true;
        game.utils.GameUtils.playAlphaAnimation(this.outroPopupContainer, { from: 0, to: 1, time: 300, timeWait: 0 });

        this.setElementVisibility(this.outroPopupAnim, true);
        this.outroPopupAnim.stop();
        this.outroPopupAnim.playAnimation('FS_appearance', false);

        this.outroPopupAnim.spine.slotContainers[
            this.outroPopupAnim.spine.slotContainers.length - 3
        ].children[0].addChild(winText);
        this.outroPopupAnim.spine.slotContainers[
            this.outroPopupAnim.spine.slotContainers.length - 3
        ].children[0].addChild(freeSpinsText);

        const playFinalAnimation = async () => {
            await game.utils.delay(600);
            this.outroPopupAnim.playAnimation('FS_idle', true);
        };

        playFinalAnimation();

        game.emit('UIComponent.SET_PARENT_GROUP_FOR_SKIPPING', { parentGroupName: 'TOP' });
        await Promise.all([
            new Promise<void>(async (resolve) => {
                await game.utils.delay(100);
                if (this.store.gameData.winAmount >= this.store.gameData.bet * 10) {
                    game.emit('WinPresentation.SHOW_WIN_PRESENTATION', { mode: 'money_middle' });
                }
                game.emit(FreeSpinsGameComponent.events.OUTRO_POPUP_START_COUNT);
                await game.utils.GameUtils.playCancelableCounter(
                    totalWin,
                    1,
                    this.store.gameData.winAmount,
                    3000,
                    createjs.Ease.sineOut
                );
                game.emit(FreeSpinsGameComponent.events.OUTRO_POPUP_END_COUNT);
                game.emit('WinPresentation.STOP_GENERATION');
                resolve();
            }),
            await game.utils.cancelableDelay(13000),
        ]);

        game.emit('UIComponent.SET_PARENT_GROUP_FOR_SKIPPING', { parentGroupName: 'OVERLAY' });
        game.emit(FreeSpinsGameComponent.events.OUTRO_POPUP_END_COUNT);
        await game.utils.delay(500);
        game.utils.removeCall(totalWin._tween);
        totalWin.options.text = this.store.toFormatMoney(this.store.gameData.winAmount);
        totalWin.updateViewOptions();

        this.outroPopupAnim.playAnimation('FS_dissappearance', false);

        game.emit(FreeSpinsGameComponent.events.START_HIDE_OUTRO_POPUP);
        game.emit('WinPresentation.STOP_GENERATION');
        game.emit('WinPresentation.HIDE_PRESENTATION');

        await game.utils.GameUtils.playAlphaAnimation(this.outroPopupContainer, {
            from: 1,
            to: 0,
            time: 500,
            timeWait: 0,
        });

        this.setElementVisibility(this.outroPopupAnim, false);
        this.outroPopupContainer.visible = false;
    }

    get store(): SlotGameStore {
        return <SlotGameStore>game.store;
    }

    get isAddedFreeSpins(): boolean {
        return Boolean(this.store.gameData.freeSpinsGame.added && this.store.gameData.freeSpinsGame.isPlaying());
    }
}

export { FreeSpinsGameComponent };

//@ts-ignore
Game.src.component.FreeSpinsGameComponent = FreeSpinsGameComponent;
